import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

export default function IntegrationsPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Integrations';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showWaveSetup, setShowWaveSetup] = useState(false);
  const [allowedClients, setAllowedClients] = useState([]);
  const [newClient, setNewClient]           = useState('');
  const [clientMsg, setClientMsg]           = useState('');
  const [waveToken, setWaveToken]       = useState('');
  const [waveBusinessId, setWaveBusinessId] = useState('');
  const [connecting, setConnecting]     = useState(false);
  const [syncing, setSyncing]           = useState(false);
  const [msg, setMsg]                   = useState('');
  const [error, setError]               = useState('');

  useEffect(() => { loadIntegrations(); loadAllowedClients(); }, []);

  async function loadAllowedClients() {
    try {
      const res = await api.get('/payments/integrations/wave/clients');
      setAllowedClients(res.data);
    } catch (err) { console.error(err); }
  }

  async function handleAddClient() {
    if (!newClient.trim()) return;
    const updated = [...allowedClients, newClient.trim()];
    try {
      await api.put('/payments/integrations/wave/clients', { allowedClients: updated });
      setAllowedClients(updated);
      setNewClient('');
      setClientMsg('Client added');
      setTimeout(() => setClientMsg(''), 2000);
    } catch (err) { setClientMsg('Failed to add'); }
  }

  async function handleRemoveClient(client) {
    const updated = allowedClients.filter(c => c !== client);
    try {
      await api.put('/payments/integrations/wave/clients', { allowedClients: updated });
      setAllowedClients(updated);
      setClientMsg('Client removed');
      setTimeout(() => setClientMsg(''), 2000);
    } catch (err) { setClientMsg('Failed to remove'); }
  }

  async function loadIntegrations() {
    try {
      const res = await api.get('/payments/integrations');
      setIntegrations(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function handleWaveConnect() {
    if (!waveToken || !waveBusinessId) {
      setError('API token and business ID required');
      return;
    }
    setConnecting(true); setError(''); setMsg('');
    try {
      const res = await api.post('/payments/integrations/wave', {
        apiToken:   waveToken,
        businessId: waveBusinessId
      });
      setMsg(`Connected to ${res.data.businessName} ✓`);
      setShowWaveSetup(false);
      setWaveToken(''); setWaveBusinessId('');
      loadIntegrations();
    } catch (err) {
      setError(err.response?.data?.error || 'Connection failed');
    } finally { setConnecting(false); }
  }

  async function handleToggle(name, currentEnabled) {
    try {
      await api.put(`/payments/integrations/${name}/toggle`, { enabled: !currentEnabled });
      setMsg(!currentEnabled ? `${name} enabled` : `${name} disabled`);
      loadIntegrations();
    } catch (err) { setError('Failed to toggle'); }
  }

  async function handleSync(name) {
    setSyncing(true); setMsg(''); setError('');
    try {
      const res = await api.post(`/payments/integrations/${name}/sync`);
      setMsg(`Synced ${res.data.synced} new invoices (${res.data.total} total unpaid)`);
      loadIntegrations();
    } catch (err) {
      setError(err.response?.data?.error || 'Sync failed');
    } finally { setSyncing(false); }
  }

  async function handleDisconnect(name) {
    if (!window.confirm(`Disconnect ${name}?`)) return;
    try {
      await api.delete(`/payments/integrations/${name}`);
      setMsg(`${name} disconnected`);
      loadIntegrations();
    } catch (err) { setError('Failed to disconnect'); }
  }

  function formatDate(ts) {
    if (!ts) return 'Never';
    return new Date(ts * 1000).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  }

  const wave = integrations.find(i => i.name === 'wave');

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Integrations</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>
        <div style={s.subtitle}>
          Connect your business tools to Thunderbolt. Integrations sync automatically every 15 minutes.
        </div>

        {msg && <div style={s.successMsg}>{msg}</div>}
        {error && <div style={s.errorMsg}>{error}</div>}

        {loading ? (
          <div style={s.loading}>Loading...</div>
        ) : (
          <>
            {/* Wave Integration Card */}
            <div style={s.card}>
              <div style={s.cardHeader}>
                <div style={s.integrationIcon}>W</div>
                <div style={s.integrationInfo}>
                  <div style={s.integrationName}>Wave</div>
                  <div style={s.integrationDesc}>
                    Sync invoices and accept Thunderbolt payments
                  </div>
                </div>
                {wave?.connected && (
                  <button
                    style={{...s.toggle, ...(wave.enabled ? s.toggleOn : {})}}
                    onClick={() => handleToggle('wave', wave.enabled)}>
                    <div style={{...s.toggleThumb, ...(wave.enabled ? s.toggleThumbOn : {})}}/>
                  </button>
                )}
              </div>

              {wave?.connected ? (
                <>
                  <div style={s.statusRow}>
                    <div style={s.statusDot(wave.enabled)}/>
                    <span style={s.statusText}>
                      {wave.enabled ? 'Active' : 'Paused'}
                    </span>
                    <span style={s.lastSync}>Last sync: {formatDate(wave.lastSyncAt)}</span>
                  </div>

                  {wave.lastSyncStatus && wave.lastSyncStatus !== 'connected' && (
                    <div style={{
                      ...s.syncStatus,
                      color: wave.lastSyncStatus === 'success' ? '#00C48C' : '#FF3B30'
                    }}>
                      {wave.lastSyncStatus}
                    </div>
                  )}

                  <div style={s.actionRow}>
                    <button style={s.syncBtn} onClick={() => handleSync('wave')} disabled={syncing}>
                      {syncing ? 'Syncing...' : '↻ Sync now'}
                    </button>
                    <button style={s.disconnectBtn} onClick={() => handleDisconnect('wave')}>
                      Disconnect
                    </button>
                  </div>

                  <div style={s.howItWorks}>
                    <div style={s.howTitle}>How it works</div>
                    <div style={s.howItem}>① Wave invoice created → Thunderbolt payment link generated</div>
                    <div style={s.howItem}>② Invoice memo updated with payment link automatically</div>
                    <div style={s.howItem}>③ Customer pays via Thunderbolt or card</div>
                    <div style={s.howItem}>④ Invoice marked paid in Wave</div>
                  </div>

                  {/* Allowed clients */}
                  <div style={{marginTop:16}}>
                    <div style={s.howTitle}>Allowed Clients</div>
                    <div style={{fontSize:12, color:'#6E6E73', marginBottom:10}}>
                      Add clients to enable Thunderbolt payments. No clients = no invoices synced.
                    </div>
                    {allowedClients.map(client => (
                      <div key={client} style={s.clientRow}>
                        <span style={s.clientEmail}>{client}</span>
                        <button style={s.clientRemove} onClick={() => handleRemoveClient(client)}>✕</button>
                      </div>
                    ))}
                    {clientMsg && <div style={{fontSize:12, color:'#00C48C', marginBottom:8}}>{clientMsg}</div>}
                    <div style={s.clientAdd}>
                      <input style={s.clientInput}
                        placeholder="Email or Wave customer ID"
                        value={newClient}
                        onChange={e => setNewClient(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddClient()}/>
                      <button style={s.clientAddBtn} onClick={handleAddClient}>Add</button>
                    </div>
                  </div>
                </>
              ) : showWaveSetup ? (
                <div style={s.setupForm}>
                  <div style={s.setupTitle}>Connect Wave</div>

                  <div style={s.field}>
                    <label style={s.label}>Wave API Token</label>
                    <input style={s.input}
                      type="password"
                      placeholder="vutRr0PLh..."
                      value={waveToken}
                      onChange={e => setWaveToken(e.target.value)}/>
                    <div style={s.hint}>
                      Get from developer.waveapps.com → Your App → Full Access Token
                    </div>
                  </div>

                  <div style={s.field}>
                    <label style={s.label}>Wave Business ID</label>
                    <input style={s.input}
                      type="text"
                      placeholder="QnVzaW5lc3M6..."
                      value={waveBusinessId}
                      onChange={e => setWaveBusinessId(e.target.value)}/>
                    <div style={s.hint}>
                      Your Wave business ID from the API
                    </div>
                  </div>

                  <div style={s.formBtns}>
                    <button style={s.cancelBtn} onClick={() => { setShowWaveSetup(false); setError(''); }}>
                      Cancel
                    </button>
                    <button style={s.connectBtn} onClick={handleWaveConnect} disabled={connecting}>
                      {connecting ? 'Connecting...' : 'Connect Wave'}
                    </button>
                  </div>
                </div>
              ) : (
                <button style={s.connectBtnFull} onClick={() => setShowWaveSetup(true)}>
                  + Connect Wave
                </button>
              )}
            </div>

            {/* Coming soon */}
            <div style={s.sectionTitle}>Coming soon</div>
            {[
              { name: 'QuickBooks', desc: 'Sync invoices and payments', icon: 'Q', color: '#2CA01C' },
              { name: 'Zapier',     desc: 'Connect 5,000+ apps',        icon: 'Z', color: '#FF4A00' },
              { name: 'Stripe',     desc: 'Advanced payment flows',     icon: 'S', color: '#6772E5' },
            ].map(item => (
              <div key={item.name} style={{...s.card, opacity:0.5}}>
                <div style={s.cardHeader}>
                  <div style={{...s.integrationIcon, background: item.color}}>{item.icon}</div>
                  <div style={s.integrationInfo}>
                    <div style={s.integrationName}>{item.name}</div>
                    <div style={s.integrationDesc}>{item.desc}</div>
                  </div>
                  <div style={s.comingSoon}>Soon</div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page:            {minHeight:'100vh', background:'#F5F5F7', paddingBottom:88},
  header:          {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px'},
  back:            {width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)'},
  headerTitle:     {fontSize:17, fontWeight:600, color:'#1D1D1F'},
  body:            {padding:'0 16px'},
  subtitle:        {fontSize:13, color:'#6E6E73', marginBottom:16, lineHeight:1.5},
  successMsg:      {background:'#E6FAF5', border:'1px solid #A7F3D0', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#00A86B', marginBottom:12},
  errorMsg:        {background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:12},
  loading:         {textAlign:'center', padding:40, color:'#6E6E73'},
  card:            {background:'#fff', borderRadius:20, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  cardHeader:      {display:'flex', alignItems:'center', gap:12, marginBottom:12},
  integrationIcon: {width:44, height:44, borderRadius:12, background:'#2196F3', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, flexShrink:0},
  integrationInfo: {flex:1},
  integrationName: {fontSize:15, fontWeight:600, color:'#1D1D1F'},
  integrationDesc: {fontSize:13, color:'#6E6E73', marginTop:2},
  toggle:          {width:44, height:26, borderRadius:13, background:'#E5E5EA', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0},
  toggleOn:        {background:'#007AFF'},
  toggleThumb:     {position:'absolute', top:3, left:3, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 4px rgba(0,0,0,0.2)'},
  toggleThumbOn:   {left:21},
  statusRow:       {display:'flex', alignItems:'center', gap:8, marginBottom:8},
  statusDot:       (enabled) => ({width:8, height:8, borderRadius:'50%', background: enabled ? '#00C48C' : '#AEAEB2', flexShrink:0}),
  statusText:      {fontSize:13, fontWeight:500, color:'#1D1D1F'},
  lastSync:        {fontSize:12, color:'#AEAEB2', marginLeft:'auto'},
  syncStatus:      {fontSize:12, marginBottom:8},
  actionRow:       {display:'flex', gap:8, marginBottom:12},
  syncBtn:         {flex:2, padding:'10px 0', background:'#EBF4FF', color:'#007AFF', border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer'},
  disconnectBtn:   {flex:1, padding:'10px 0', background:'#FFF0EF', color:'#FF3B30', border:'none', borderRadius:10, fontSize:13, fontWeight:500, cursor:'pointer'},
  howItWorks:      {background:'#F5F5F7', borderRadius:12, padding:12},
  howTitle:        {fontSize:12, fontWeight:600, color:'#6E6E73', marginBottom:8},
  howItem:         {fontSize:12, color:'#1D1D1F', marginBottom:4, lineHeight:1.5},
  setupForm:       {marginTop:4},
  setupTitle:      {fontSize:15, fontWeight:600, marginBottom:16},
  field:           {marginBottom:14},
  label:           {display:'block', fontSize:13, fontWeight:500, color:'#1D1D1F', marginBottom:6},
  input:           {width:'100%', padding:'12px 14px', border:'1.5px solid #F0F0F0', borderRadius:10, fontSize:14, background:'#FAFAFA', outline:'none', boxSizing:'border-box'},
  hint:            {fontSize:11, color:'#AEAEB2', marginTop:4},
  formBtns:        {display:'flex', gap:8},
  cancelBtn:       {flex:1, padding:12, background:'#F5F5F7', border:'none', borderRadius:10, fontSize:14, fontWeight:500, cursor:'pointer'},
  connectBtn:      {flex:2, padding:12, background:'#007AFF', color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer'},
  connectBtnFull:  {width:'100%', padding:14, background:'#F5F5F7', border:'1.5px dashed #AEAEB2', borderRadius:12, fontSize:14, fontWeight:500, color:'#6E6E73', cursor:'pointer'},
  sectionTitle:    {fontSize:12, fontWeight:600, color:'#AEAEB2', marginBottom:8, textTransform:'uppercase', letterSpacing:0.8},
  comingSoon:      {fontSize:11, fontWeight:600, color:'#AEAEB2', background:'#F5F5F7', padding:'4px 8px', borderRadius:6},
  clientRow:       {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 10px', background:'#F5F5F7', borderRadius:8, marginBottom:6},
  clientEmail:     {fontSize:13, color:'#1D1D1F'},
  clientRemove:    {background:'none', border:'none', color:'#FF3B30', fontSize:14, cursor:'pointer', fontWeight:600},
  clientAdd:       {display:'flex', gap:8, marginTop:8},
  clientInput:     {flex:1, padding:'8px 12px', border:'1.5px solid #F0F0F0', borderRadius:8, fontSize:13, outline:'none'},
  clientAddBtn:    {padding:'8px 14px', background:'#007AFF', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:500, cursor:'pointer'}
};
